import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const MetricCard = ({ 
  title, 
  value, 
  previousValue, 
  icon: Icon, 
  color = 'blue',
  format = 'number',
  suffix = '',
  trend = true,
  darkMode,
  tooltip
}) => {
  
  const formatValue = (val) => {
    if (format === 'percentage') return `${val}%`;
    if (format === 'currency') return `R$ ${val.toLocaleString('pt-BR')}`;
    if (format === 'time') return `${val}s`;
    return val.toLocaleString('pt-BR');
  };

  const calculateTrend = () => {
    if (!trend || previousValue === undefined || previousValue === 0) return null;
    
    const change = ((value - previousValue) / previousValue) * 100;
    const isPositive = change > 0;
    const isNeutral = change === 0;
    
    return {
      value: Math.abs(change).toFixed(1),
      isPositive,
      isNeutral,
      icon: isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown
    };
  };

  const trendData = calculateTrend();
  
  const colorClasses = {
    blue: darkMode ? 'from-blue-600 to-blue-700' : 'from-blue-500 to-blue-600',
    green: darkMode ? 'from-green-600 to-green-700' : 'from-green-500 to-green-600',
    purple: darkMode ? 'from-purple-600 to-purple-700' : 'from-purple-500 to-purple-600',
    orange: darkMode ? 'from-orange-600 to-orange-700' : 'from-orange-500 to-orange-600',
    red: darkMode ? 'from-red-600 to-red-700' : 'from-red-500 to-red-600',
    indigo: darkMode ? 'from-indigo-600 to-indigo-700' : 'from-indigo-500 to-indigo-600'
  };

  const trendColorClasses = {
    positive: darkMode ? 'text-green-400' : 'text-green-600',
    negative: darkMode ? 'text-red-400' : 'text-red-600',
    neutral: darkMode ? 'text-gray-400' : 'text-gray-500'
  };

  return (
    <div className={`rounded-lg shadow-sm border p-4 sm:p-6 hover:shadow-md transition-shadow relative group ${
      darkMode 
        ? 'bg-zinc-800 border-gray-700 hover:border-gray-600' 
        : 'bg-white border-gray-200 hover:border-gray-300'
    }`}>
      {tooltip && (
        <div className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg text-xs max-w-[220px] text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 ${
          darkMode ? 'bg-zinc-700 text-gray-200' : 'bg-gray-800 text-white'
        }`}>
          {tooltip}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className={`text-xs sm:text-sm font-medium ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {title}
          </p>
          <div className="flex items-baseline mt-2">
            <p className={`text-xl sm:text-2xl font-semibold ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {formatValue(value)}{suffix}
            </p>
            {trendData && (
              <div className={`ml-2 flex items-center text-sm ${
                trendData.isNeutral 
                  ? trendColorClasses.neutral
                  : trendData.isPositive 
                    ? trendColorClasses.positive
                    : trendColorClasses.negative
              }`}>
                <trendData.icon className="h-4 w-4 mr-1" />
                <span>{trendData.value}%</span>
              </div>
            )}
          </div>
        </div>
        
        <div className={`p-2 sm:p-3 rounded-lg bg-gradient-to-r ${colorClasses[color]}`}>
          <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
        </div>
      </div>
    </div>
  );
};

export default MetricCard;